import React from 'react';

import FaExternalLink from 'react-icons/lib/fa/external-link';
import FaLock from 'react-icons/lib/fa/lock';

import {
    FadeInOutContainer,
    FadeInOut,
} from 'wanderu-animations';
import A from 'wanderu-component-react-router-link';
import {
    Typography,
    WDSButton,
} from 'wanderu-components';
import { localize } from 'wanderu-i18n';

import styles from './BookButton.scss';

import type { TFunction } from 'i18next';

const FADE_DURATION = 250;

interface ButtonContentsProps {
    className?: string;
    deeplink?: boolean;
    t?: TFunction;
}

function ButtonContents({
    className,
    deeplink = false,
    t = () => '',
}: ButtonContentsProps) {
    return (
        <div className={`${className} ${styles.buttonContents}`}>
            <div className={styles.buttonText}>
                <span>{t<string>('checkout')}</span>
            </div>
            {deeplink && (
                <FaExternalLink className={styles.deepLinkIcon} />
            )}
        </div>
    );
}

const LocalizedButtonContents = localize(ButtonContents, ['t'], 'baseApp');

interface DeepLinkData {
    method?: 'POST' | 'GET';
    href?: string;
    host?: string;
    body?: {
        reqXML?: string;
        partnerId?: string;
    };
}

interface BookButtonProps {
    animateHeight?: boolean;
    busy?: boolean;
    buttonContentsClassName?: string;
    className?: string;
    deepLinkData?: DeepLinkData;
    deeplink?: boolean;
    deeplinkExists?: boolean;
    disabled?: boolean;
    href?: string;
    onClick?: (isDeeplink: boolean, href: string) => void;
    show?: boolean;
    t: TFunction;
}

function BookButton({
    animateHeight = false,
    busy = false,
    buttonContentsClassName = '',
    className = '',
    deepLinkData = {},
    deeplink = false,
    deeplinkExists = false,
    disabled = false,
    href = '',
    onClick = () => undefined,
    show = false,
    t,
}: BookButtonProps) {
    const linkButtonOpts = deeplink || deeplinkExists
        ? { target: '_blank' as const }
        : {};
    const buttonProps = {
        className: styles.bookButton,
        color: 'secondary',
        disabled: disabled || busy,
        fullWidth: true,
        label: t<string>('checkout'),
        ...linkButtonOpts,
    };

    function onSubmit() {
        onClick(deeplink, href);
    }

    return (
        <FadeInOutContainer
            animateHeight={animateHeight}
            className={className}
            duration={FADE_DURATION}
        >
            <FadeInOut show={show}>
                <WDSButton
                    component={A}
                    href={href}
                    onClick={onSubmit}
                    underline="none"
                    {...buttonProps}
                >
                    <LocalizedButtonContents
                        className={buttonContentsClassName}
                        deeplink={deeplink}
                    />
                </WDSButton>
                {deeplink && (
                    <div className={styles.helperText}>
                        <Typography variant="body2">
                            {t<string>('at-url', { website: deepLinkData.host })}
                        </Typography>
                        <FaLock className={styles.secure} />
                    </div>
                )}
            </FadeInOut>
        </FadeInOutContainer>
    );
}

export {
    BookButton,
    type BookButtonProps,
    ButtonContents,
};

export default localize(BookButton, ['t'], 'baseApp');
